import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  getService,
  listPackages,
  listPortfolioFeatured,
  listPortfolioPaginated,
  listFaqs,
} from "../lib/api";
import { openLightbox } from "../lib/lightbox-store";
import PackageCard from "../components/shared/PackageCard";
import { SkeletonCard, SkeletonImage } from "../components/shared/Skeleton";
import EmptyState from "../components/shared/EmptyState";
import ErrorBanner from "../components/shared/ErrorBanner";
import { t } from "../lib/i18n";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [packages, setPackages] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [allPortfolio, setAllPortfolio] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSubcat, setActiveSubcat] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const svc = await getService(slug);
        if (cancelled) return;
        setService(svc);
        const [pkgs, port, portAll, faqList] = await Promise.all([
          listPackages({ service_id: svc.id, limit: 12 }),
          listPortfolioFeatured({ service_id: svc.id, limit: 8 }),
          listPortfolioPaginated({ service_id: svc.id, limit: 96 }),
          listFaqs({ service_id: svc.id }),
        ]);
        if (cancelled) return;
        setPackages(pkgs);
        setPortfolio(port);
        setAllPortfolio((portAll && portAll.items) || []);
        setFaqs(faqList);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const filteredPackages = useMemo(() => {
    if (!activeSubcat) return packages;
    const key = activeSubcat.toLowerCase();
    return packages.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(key) ||
        (p.summary || "").toLowerCase().includes(key),
    );
  }, [packages, activeSubcat]);

  if (loading) {
    return (
      <section className="mx-auto max-w-6xl px-5 pt-20 pb-24 sm:px-6 sm:pt-28">
        <SkeletonImage className="h-72 w-full" />
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </section>
    );
  }

  if (error || !service) {
    return (
      <section className="mx-auto max-w-6xl px-5 pt-20 pb-24 sm:px-6 sm:pt-28">
        <ErrorBanner message="Service not found or temporarily unavailable." />
      </section>
    );
  }

  return (
    <article data-testid="service-detail-page" className="pb-24">
      {/* Hero collage */}
      <section className="mx-auto max-w-6xl px-5 pt-12 sm:px-6 sm:pt-20">
        <p className="text-xs uppercase tracking-[0.32em] text-[#C8A24B]">
          {t("brand.name")}
        </p>
        <h1
          data-testid="service-detail-name"
          className="mt-3 font-display text-4xl text-[#1A1A1A] sm:text-5xl lg:text-6xl"
        >
          {service.name}
        </h1>
        <p className="mt-4 max-w-2xl text-base text-[#1A1A1A]/65 sm:text-lg">
          {service.description}
        </p>

        <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4">
          {portfolio.slice(0, 3).map((img, i) => (
            <button
              key={img.id}
              type="button"
              data-testid={`service-hero-image-${i}`}
              onClick={() => openLightbox(portfolio, i)}
              className={[
                "block aspect-[3/4] overflow-hidden rounded-md bg-[#EAE3D2] focus:outline-none focus:ring-2 focus:ring-[#C8A24B]",
                i === 1 ? "translate-y-6 sm:translate-y-10" : "",
              ].join(" ")}
            >
              <img
                src={img.url}
                alt={img.title || `${service.name} sample ${i + 1}`}
                className="h-full w-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
            </button>
          ))}
        </div>
      </section>

      {/* Subcategory pills */}
      {service.subcategories?.length ? (
        <section className="mx-auto mt-20 max-w-6xl px-5 sm:mt-24 sm:px-6">
          <h2 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">
            {t("serviceDetail.subcategories")}
          </h2>
          <ul
            data-testid="service-subcategories"
            className="mt-6 flex flex-wrap gap-2"
          >
            <li>
              <button
                type="button"
                data-testid="subcat-pill-all"
                onClick={() => setActiveSubcat(null)}
                className={[
                  "rounded-full border px-4 py-2 text-xs uppercase tracking-[0.18em] transition",
                  activeSubcat === null
                    ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF7F2]"
                    : "border-[#1A1A1A]/15 text-[#1A1A1A] hover:border-[#C8A24B] hover:text-[#C8A24B]",
                ].join(" ")}
              >
                All
              </button>
            </li>
            {service.subcategories.map((sub) => (
              <li key={sub.id}>
                <button
                  type="button"
                  data-testid={`subcat-pill-${sub.slug}`}
                  onClick={() => setActiveSubcat(sub.name)}
                  className={[
                    "rounded-full border px-4 py-2 text-xs uppercase tracking-[0.18em] transition",
                    activeSubcat === sub.name
                      ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF7F2]"
                      : "border-[#1A1A1A]/15 text-[#1A1A1A] hover:border-[#C8A24B] hover:text-[#C8A24B]",
                  ].join(" ")}
                >
                  {sub.name}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Packages */}
      <section className="mx-auto mt-16 max-w-6xl px-5 sm:px-6">
        <h2 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">
          {t("serviceDetail.packages")}
        </h2>
        <div className="mt-8">
          {filteredPackages.length ? (
            <ul
              data-testid="service-packages-list"
              className="grid grid-cols-1 gap-6 md:grid-cols-3"
            >
              {filteredPackages.map((p) => (
                <li key={p.id}>
                  <PackageCard pkg={p} coverImage={portfolio[0]} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No packages match this filter"
              subtitle="Reset to 'All' to see every package in this service."
              testid="service-packages-empty"
            />
          )}
        </div>
      </section>

      {/* More from this service — full portfolio grid + lightbox */}
      {allPortfolio.length ? (
        <section
          data-testid="service-portfolio-grid"
          className="mx-auto mt-20 max-w-6xl px-5 sm:mt-24 sm:px-6"
        >
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#C8A24B]">
            Browse our work
          </p>
          <h2 className="mt-2 font-display text-3xl text-[#1A1A1A] sm:text-4xl">
            More from this service
          </h2>
          <ul className="mt-8 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-4">
            {allPortfolio.map((img, i) => (
              <li key={img.id}>
                <button
                  type="button"
                  data-testid={`service-portfolio-item-${img.id}`}
                  onClick={() => openLightbox(allPortfolio, i)}
                  aria-label={img.title || `${service.name} sample ${i + 1}`}
                  className="group block aspect-[4/5] w-full overflow-hidden rounded-md bg-[#EAE3D2] focus:outline-none focus:ring-2 focus:ring-[#C8A24B]"
                >
                  <img
                    src={img.thumbnail_url || img.url}
                    alt={img.title || `${service.name} sample ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* FAQs */}
      {faqs.length ? (
        <section className="mx-auto mt-20 max-w-3xl px-5 sm:px-6">
          <h2 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">
            {t("serviceDetail.faqs")}
          </h2>
          <Accordion type="single" collapsible className="mt-6">
            {faqs.slice(0, 6).map((q) => (
              <AccordionItem
                key={q.id}
                value={q.id}
                data-testid={`service-faq-${q.id}`}
                className="border-[#EAE3D2]"
              >
                <AccordionTrigger className="text-left text-base text-[#1A1A1A]">
                  {q.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#1A1A1A]/75">
                  {q.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ) : null}

      {/* CTA */}
      <section className="mx-auto mt-20 max-w-6xl px-5 sm:px-6">
        <div
          data-testid="service-cta-card"
          className="rounded-lg border border-[#EAE3D2] bg-white p-8 sm:p-12"
        >
          <h3 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">
            {t("serviceDetail.cta.title")}
          </h3>
          <p className="mt-3 max-w-xl text-sm text-[#1A1A1A]/65 sm:text-base">
            {t("serviceDetail.cta.subtitle")}
          </p>
          <Link
            to={`/plan?service=${service.slug}`}
            data-testid="service-cta-button"
            className="mt-6 inline-flex items-center rounded-full bg-[#C8A24B] px-7 py-3 text-xs uppercase tracking-[0.22em] text-[#1A1A1A] transition hover:bg-[#dbb359]"
          >
            {t("serviceDetail.cta.button")}
          </Link>
        </div>
      </section>
    </article>
  );
}
